import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useSocketEvent } from '../hooks/useSocket';
import api from '../services/api';
import {
  IconFolder, IconLayers, IconImage, IconVideo, IconCalendar,
  IconCheck, IconMessage, IconPlus, IconEye, IconUsers, IconSparkles
} from '../components/Icons';

if (typeof document !== 'undefined' && !document.getElementById('avatar-wave-css')) {
  const style = document.createElement('style');
  style.id = 'avatar-wave-css';
  style.innerHTML = `
    @keyframes wavingCharacterClean {
      0% { transform: rotate(0deg); }
      15% { transform: rotate(10deg); }
      30% { transform: rotate(-6deg); }
      45% { transform: rotate(10deg); }
      60% { transform: rotate(-4deg); }
      75% { transform: rotate(6deg); }
      90% { transform: rotate(0deg); }
      100% { transform: rotate(0deg); }
    }
    @keyframes bdayCakeBounce {
      0%, 100% { transform: scale(1) translateY(0); }
      50% { transform: scale(1.08) translateY(-4px) rotate(4deg); }
    }
    @keyframes bdayGlowPulse {
      0%, 100% {
        box-shadow: 0 16px 40px rgba(109, 40, 217, 0.35), 0 0 20px rgba(236, 72, 153, 0.2);
      }
      50% {
        box-shadow: 0 22px 55px rgba(109, 40, 217, 0.5), 0 0 35px rgba(251, 191, 36, 0.45);
      }
    }
    @keyframes partySparkle {
      0%, 100% { opacity: 0.3; transform: scale(0.8); }
      50% { opacity: 1; transform: scale(1.2); }
    }
  `;
  document.head.appendChild(style);
}

const MEDAL_EMOJIS: Record<number, string> = {
  0: 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f947.png', // Oro 🥇
  1: 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f948.png', // Plata 🥈
  2: 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f949.png', // Bronce 🥉
};

const isFemaleUser = (user: any): boolean => {
  if (user?.gender) {
    const g = String(user.gender).toLowerCase();
    if (['f', 'female', 'mujer', 'femenino'].includes(g)) return true;
    if (['m', 'male', 'hombre', 'masculino'].includes(g)) return false;
  }
  const name = (user?.firstName || '').trim().toLowerCase().split(' ')[0];
  if (!name) return false;
  const femaleNames = new Set([
    'maria', 'maría', 'ana', 'sofia', 'sofía', 'valentina', 'camila', 'isabella', 'valeria', 'daniela',
    'mariana', 'gabriela', 'laura', 'catalina', 'paula', 'juliana', 'natalia', 'andrea', 'victoria',
    'sara', 'lucia', 'lucía', 'elena', 'martina', 'carolina', 'diana', 'claudia', 'patricia', 'alejandra',
    'fernanda', 'jessica', 'paola', 'monica', 'mónica', 'karen', 'lorena', 'vanessa', 'estefania', 'adriana',
    'silvia', 'luisa', 'gloria', 'tatiana', 'sandra', 'angela', 'ángela', 'veronica', 'verónica', 'manuela'
  ]);
  if (femaleNames.has(name)) return true;
  if (name.endsWith('a') && !['joshua', 'luca', 'sasha'].includes(name)) return true;
  return false;
};


// COMPONENTE DE ANIMACIÓN RULETA DE NÚMEROS (ODOMETER / ROULETTE COUNTER CON TIPOGRAFÍA PERFECTA)
const RouletteNumber: React.FC<{ value: number | string; duration?: number }> = ({ value, duration = 1300 }) => {
  const target = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const [display, setDisplay] = useState<number>(0);

  useEffect(() => {
    if (target === 0) {
      // Para valor 0, giro rápido inicial que aterriza en 0
      const frames = [6, 3, 1, 0];
      let idx = 0;
      const interval = setInterval(() => {
        if (idx < frames.length) {
          setDisplay(frames[idx]);
          idx++;
        } else {
          clearInterval(interval);
          setDisplay(0);
        }
      }, 70);
      return () => clearInterval(interval);
    }

    let start: number | null = null;
    let animId: number;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      
      // Curva de desaceleración tipo ruleta (easeOutExpo)
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentVal = Math.round(ease * target);
      
      setDisplay(currentVal);

      if (progress < 1) {
        animId = requestAnimationFrame(animate);
      } else {
        setDisplay(target);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [target, duration]);

  return <span>{display}</span>;
};

// COMPONENTE DE BARRA DE PROGRESO ANIMADA CON RULETA NUMÉRICA
const AnimatedBrandProgress: React.FC<{ brand: any; index: number }> = ({ brand: b, index }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setWidth(b.pct);
    }, 120 + index * 100);
    return () => clearTimeout(timer);
  }, [b.pct, index]);

  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: '14px',
      background: 'rgba(255, 255, 255, 0.45)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      border: '1px solid rgba(255, 255, 255, 0.65)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>{b.name}</span>
        <span style={{
          fontSize: '11px',
          fontWeight: 800,
          color: b.pct >= 80 ? '#16a34a' : b.pct >= 40 ? '#0070f3' : '#64748b',
          background: b.pct >= 80 ? 'rgba(22, 163, 74, 0.1)' : b.pct >= 40 ? 'rgba(0, 112, 243, 0.1)' : 'rgba(100, 116, 139, 0.1)',
          padding: '2px 8px',
          borderRadius: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px'
        }}>
          <RouletteNumber value={b.totalApproved} duration={1200} />
          <span>/ {b.totalScope || '∞'} (</span>
          <RouletteNumber value={b.pct} duration={1200} />
          <span>%)</span>
        </span>
      </div>
      <div style={{ width: '100%', height: '8px', borderRadius: '6px', background: 'rgba(226, 232, 240, 0.6)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.7)' }}>
        <div style={{
          width: `${width}%`,
          height: '100%',
          background: b.pct >= 80 ? 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' : 'linear-gradient(90deg, #0070f3 0%, #38bdf8 100%)',
          borderRadius: '6px',
          boxShadow: 'none',
          transition: `width ${1.3 + index * 0.15}s cubic-bezier(0.16, 1, 0.3, 1)`
        }} />
      </div>
    </div>
  );
};

const isBirthdayToday = (birthDateStr: any): boolean => {
  if (!birthDateStr) return false;
  const now = new Date();
  const utcNow = new Date(now.getTime() - (5 * 3600000));
  const tDay = utcNow.getUTCDate();
  const tMonth = utcNow.getUTCMonth() + 1;

  if (typeof birthDateStr === 'string' && birthDateStr.includes('-')) {
    const parts = birthDateStr.split('T')[0].split('-');
    if (parts.length >= 3) {
      const bMonth = parseInt(parts[1], 10);
      const bDay = parseInt(parts[2], 10);
      return bMonth === tMonth && bDay === tDay;
    }
  }
  const d = new Date(birthDateStr);
  return (d.getUTCDate() === tDay && d.getUTCMonth() + 1 === tMonth) ||
         (d.getDate() === tDay && d.getMonth() + 1 === tMonth);
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = ['ADMIN', 'CUENTAS'].includes(user?.role || '');
  const isCreative = ['DISENO', 'CONTENIDO', 'ESTRATEGIA'].includes(user?.role || '');

  // Lógica Módulo 4: Cumpleaños del Colaborador
  const isMyBirthday = Boolean(data?.isMyBirthday || isBirthdayToday(user?.birthDate));
  const otherBirthdays = (data?.todaysBirthdays || []).filter((b: any) => b.id !== user?.id);

  const triggerBirthdayConfetti = () => {
    try {
      const duration = 3.5 * 1000;
      const animationEnd = Date.now() + duration;
      const colors = ['#0070f3', '#ec4899', '#8b5cf6', '#f59e0b', '#10b981', '#fbbf24', '#ffffff'];

      const interval: any = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 40 * (timeLeft / duration);
        confetti({
          particleCount,
          spread: 80,
          origin: { x: Math.random() * 0.4 + 0.1, y: Math.random() * 0.4 + 0.1 },
          colors,
          zIndex: 9999
        });
        confetti({
          particleCount,
          spread: 80,
          origin: { x: Math.random() * 0.4 + 0.5, y: Math.random() * 0.4 + 0.1 },
          colors,
          zIndex: 9999
        });
      }, 250);
    } catch (e) {
      console.error('Confetti error:', e);
    }
  };

  useEffect(() => {
    if (isMyBirthday) {
      const timer = setTimeout(() => {
        triggerBirthdayConfetti();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isMyBirthday]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard');
      setData(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Actualización en tiempo real por WebSockets
  useSocketEvent('refresh:dashboard', loadData);
  useSocketEvent('refresh:pieces', loadData);

  const todayStr = new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const myMetrics = data?.myMetrics || { unitsDeliveredMonth: 0, inReviewCount: 0, inCorrectionCount: 0, approvedMonth: 0 };
  const priorities = data?.myPriorities || [];
  const feedbacks = data?.myRecentFeedback || [];
  const collaborators = data?.collaborators || [];
  const brandsProgress = data?.brandsProgress || [];
  const glassCardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.72) 0%, rgba(255, 255, 255, 0.42) 100%)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderRadius: '22px',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    boxShadow: '0 8px 32px 0 rgba(15, 42, 74, 0.06), inset 0 1px 2px 0 rgba(255, 255, 255, 0.9)',
    transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
  };

  const glassInnerItemStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03)'
  };

  return (
    <div className="zoik-page-container" style={{
      display: 'flex',
      height: '100vh',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflow: 'hidden',
      background: 'radial-gradient(at 0% 0%, rgba(0, 112, 243, 0.07) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(38, 136, 201, 0.08) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(99, 102, 241, 0.06) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%), #edf2f8',
      fontFamily: '"Montserrat", sans-serif'
    }}>
      <style>{`
        @media (max-width: 960px) {
          .dash-section-grid-2 {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
        }
        @media (max-width: 680px) {
          .dash-hero {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 20px 18px !important;
            gap: 16px !important;
          }
          .dash-kpi-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .dash-kpi-number {
            font-size: 28px !important;
          }
        }
        @media (max-width: 420px) {
          .dash-kpi-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <Sidebar />

      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        padding: '32px 40px',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {/* 1. HERO HEADER PERSONALIZADO (MODIFICADO EN CUMPLEAÑOS) */}
        <div
          className="dash-hero"
          style={{
            background: isMyBirthday
              ? 'linear-gradient(135deg, rgba(24, 13, 54, 0.95) 0%, rgba(67, 24, 114, 0.92) 30%, rgba(91, 33, 182, 0.9) 65%, rgba(190, 24, 93, 0.88) 100%)'
              : 'linear-gradient(135deg, rgba(15, 42, 74, 0.92) 0%, rgba(23, 61, 107, 0.85) 100%)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            borderRadius: '24px',
            border: isMyBirthday
              ? '1.5px solid rgba(251, 191, 36, 0.55)'
              : '1px solid rgba(255, 255, 255, 0.22)',
            padding: '28px 36px',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: isMyBirthday
              ? '0 20px 50px rgba(109, 40, 217, 0.35), inset 0 1px 2px rgba(255, 255, 255, 0.4)'
              : '0 16px 40px rgba(15, 42, 74, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.3)',
            animation: isMyBirthday ? 'bdayGlowPulse 4s infinite ease-in-out' : 'none',
            marginBottom: '28px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Destellos festivos decorativos */}
          {isMyBirthday && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden' }}>
              <span style={{ position: 'absolute', top: '10%', left: '8%', fontSize: '18px', animation: 'partySparkle 2s infinite ease-in-out' }}>✨</span>
              <span style={{ position: 'absolute', top: '18%', right: '14%', fontSize: '20px', animation: 'partySparkle 2.5s infinite ease-in-out 0.4s' }}>⭐</span>
              <span style={{ position: 'absolute', bottom: '12%', left: '32%', fontSize: '22px', animation: 'partySparkle 3s infinite ease-in-out 0.8s' }}>🎉</span>
              <span style={{ position: 'absolute', bottom: '20%', right: '8%', fontSize: '24px', animation: 'partySparkle 2.2s infinite ease-in-out 0.2s' }}>🎈</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', zIndex: 1 }}>
            {/* ICONO CON ANIMACIÓN FESTIVA */}
            <div
              style={{
                width: '68px',
                height: '68px',
                borderRadius: '22px',
                background: isMyBirthday
                  ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(236, 72, 153, 0.25) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.06) 100%)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: isMyBirthday
                  ? '2px solid rgba(251, 191, 36, 0.65)'
                  : '1.5px solid rgba(255, 255, 255, 0.3)',
                boxShadow: isMyBirthday ? '0 8px 24px rgba(251, 191, 36, 0.35)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                animation: isMyBirthday ? 'bdayCakeBounce 2s infinite ease-in-out' : 'none'
              }}
            >
              {isMyBirthday ? (
                <span style={{ fontSize: '38px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.3))' }}>🎂</span>
              ) : (
                <img
                  src={
                    isFemaleUser(user)
                      ? 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f64b-200d-2640-fe0f.png'
                      : 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f64b-200d-2642-fe0f.png'
                  }
                  alt="Saludo"
                  style={{
                    width: '46px',
                    height: '46px',
                    objectFit: 'contain',
                    animation: 'wavingCharacterClean 2.2s infinite ease-in-out',
                    transformOrigin: '70% 35%',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))'
                  }}
                />
              )}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: isMyBirthday ? '#fbbf24' : 'rgba(255,255,255,0.75)', textTransform: 'capitalize' }}>
                  {todayStr}
                </div>
                {isMyBirthday && (
                  <span
                    style={{
                      background: 'rgba(251, 191, 36, 0.25)',
                      border: '1px solid rgba(251, 191, 36, 0.6)',
                      color: '#fbbf24',
                      fontSize: '10px',
                      fontWeight: 900,
                      padding: '2px 8px',
                      borderRadius: '8px',
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase'
                    }}
                  >
                    🎉 ¡Día Especial!
                  </span>
                )}
              </div>

              <h1 style={{ margin: 0, fontSize: isMyBirthday ? '26px' : '24px', fontWeight: 900, letterSpacing: '-0.5px' }}>
                {isMyBirthday
                  ? `¡Felicidades, cumpliste años, ${user?.firstName || 'Tripulante'}! 🎂🎉`
                  : `¡Hola, ${user?.firstName ? `${user.firstName}${user?.lastName ? ` ${user.lastName}` : ''}` : 'Creativo'}!`
                }
              </h1>

              <div style={{ fontSize: '13px', color: isMyBirthday ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.88)', marginTop: '4px', maxWidth: isMyBirthday ? '640px' : 'none' }}>
                {isMyBirthday
                  ? `¡Hoy toda la tripulación de ZOIK Agency celebra tu vida, tu creatividad y tu talento! Gracias por ser parte fundamental de nuestro equipo. ¡Que tengas un día grandioso y lleno de éxitos! 🚀✨`
                  : (isCreative
                      ? `Tienes ${priorities.length} entregable(s) en tu bandeja de trabajo activa.`
                      : `Supervisando ${brandsProgress.length} marcas activas y la producción del equipo.`)
                }
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
            {isMyBirthday && (
              <button
                onClick={triggerBirthdayConfetti}
                style={{
                  padding: '12px 22px',
                  borderRadius: '14px',
                  border: '1.5px solid rgba(251, 191, 36, 0.6)',
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  color: '#0f2a4a',
                  fontSize: '13px',
                  fontWeight: 900,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 6px 20px rgba(251, 191, 36, 0.4)',
                  transition: 'all 0.15s ease',
                  fontFamily: '"Montserrat", sans-serif'
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px) scale(1.02)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0) scale(1)'; }}
              >
                <span>🥳</span> ¡Lanzar Celebración!
              </button>
            )}

            <button
              onClick={() => navigate('/content')}
              style={{
                padding: '12px 22px',
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: isMyBirthday
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                color: 'white',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: 'none',
                transition: 'all 0.15s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
            >
              <IconPlus size={16} color="#ffffff" /> Ir a Contenido
            </button>
          </div>
        </div>

        {/* BANNER FESTIVO SI OTROS COLABORADORES CUMPLEN AÑOS HOY */}
        {otherBirthdays.map((bday: any) => (
          <div
            key={bday.id}
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(253, 242, 248, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '16px 24px',
              border: '1.5px solid rgba(244, 114, 182, 0.4)',
              boxShadow: '0 8px 24px rgba(236, 72, 153, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '28px',
              flexWrap: 'wrap',
              gap: '14px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ position: 'relative' }}>
                {bday.avatar ? (
                  <img src={bday.avatar} alt={bday.firstName} style={{ width: '46px', height: '46px', borderRadius: '14px', objectFit: 'cover', border: '2px solid #ec4899' }} />
                ) : (
                  <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '18px' }}>
                    {bday.firstName[0]}
                  </div>
                )}
                <span style={{ position: 'absolute', top: '-8px', right: '-8px', fontSize: '18px' }}>🎂</span>
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 900, color: '#0f2a4a' }}>
                  🎉 ¡Hoy está de cumpleaños {bday.firstName} {bday.lastName}!
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                  Tripulante {bday.position ? `· ${bday.position}` : `· ${bday.role}`} · ¡Envíale tus felicitaciones en su día especial!
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/chat')}
              style={{
                padding: '9px 18px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                color: 'white',
                fontWeight: 800,
                fontSize: '12px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(236, 72, 153, 0.3)',
                fontFamily: '"Montserrat", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              💬 Felicitar en el Chat
            </button>
          </div>
        ))}

        {/* 2. TARJETAS KPIS TRASLÚCIDAS (GLASSMORPHISM PURO) */}
        <div className="dash-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '32px' }}>
          {/* KPI 1: Entregables del Mes */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {isCreative ? 'Mis Entregables (Mes)' : 'Producción Total'}
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconLayers size={18} color="#0f2a4a" />
              </div>
            </div>
            <div className="dash-kpi-number" style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={myMetrics.unitsDeliveredMonth} />
            </div>
            <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }}></span>
              Unidades aprobadas este mes
            </div>
          </div>

          {/* KPI 2: En Ajustes / Corrección */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Ajustes Pendientes
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconMessage size={18} color="#0f2a4a" />
              </div>
            </div>
            <div className="dash-kpi-number" style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={myMetrics.inCorrectionCount} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              Piezas esperando ajustes
            </div>
          </div>

          {/* KPI 3: En Revisión */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                En Revisión
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconEye size={18} color="#0f2a4a" />
              </div>
            </div>
            <div className="dash-kpi-number" style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={myMetrics.inReviewCount} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              Esperando visto bueno
            </div>
          </div>

          {/* KPI 4: Aprobadas */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Piezas Aprobadas
              </span>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck size={18} color="#0f2a4a" />
              </div>
            </div>
            <div className="dash-kpi-number" style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={myMetrics.approvedMonth} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              Listas para publicación
            </div>
          </div>
        </div>

        {/* 3. CONTENEDOR PRINCIPAL: BANDEJA DE TRABAJO & FEEDBACK */}
        <div className="dash-section-grid-2" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '24px', marginBottom: '32px' }}>
          
          {/* BANDEJA DE TRABAJO INMEDIATO */}
          <div style={{ ...glassCardStyle, padding: '26px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconCalendar size={18} color="#0f2a4a" />
                </div>
                Prioridades y Entregas
              </h2>
              <span style={{
                fontSize: '11px',
                fontWeight: 800,
                color: '#0070f3',
                background: 'rgba(0, 112, 243, 0.08)',
                padding: '4px 12px',
                borderRadius: '20px',
                border: '1px solid rgba(0, 112, 243, 0.15)'
              }}>
                {priorities.length} pendientes
              </span>
            </div>

            {priorities.length === 0 ? (
              <div style={{
                padding: '36px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '13px',
                background: 'rgba(255, 255, 255, 0.35)',
                borderRadius: '16px',
                border: '1px dashed rgba(203, 213, 225, 0.8)'
              }}>
                ¡Estás al día! No tienes entregables pendientes por el momento.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {priorities.map((piece: any) => (
                  <div
                    key={piece.id}
                    onClick={() => navigate(`/content?pieceId=${piece.id}`)}
                    style={{
                      ...glassInnerItemStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'rgba(0, 112, 243, 0.6)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 18px rgba(15, 42, 74, 0.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.7)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.55)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 42, 74, 0.03)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(226, 232, 240, 0.7)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.8)' }}>
                        {piece.fileUrl ? (
                          <img src={piece.fileUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <IconImage size={20} color="#64748b" />
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#0070f3', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                          {piece.brand?.name || 'Marca'}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a', marginTop: '2px' }}>
                          {piece.title || 'Sin título'}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '5px 12px',
                        borderRadius: '20px',
                        background: piece.status === 'REVISION_INTERNA' ? 'rgba(254, 243, 199, 0.85)' : 'rgba(224, 231, 255, 0.85)',
                        color: piece.status === 'REVISION_INTERNA' ? '#b45309' : '#3730a3',
                        border: piece.status === 'REVISION_INTERNA' ? '1px solid rgba(251, 191, 36, 0.5)' : '1px solid rgba(199, 210, 254, 0.5)'
                      }}>
                        {piece.status === 'REVISION_INTERNA' ? 'En Revisión' : piece.status}
                      </span>
                      <IconEye size={16} color="#64748b" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MURO DE AJUSTES Y RETROALIMENTACIÓN RECIENTE */}
          <div style={{ ...glassCardStyle, padding: '26px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(239, 246, 255, 0.85)', border: '1px solid rgba(191, 219, 254, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconMessage size={18} color="#0f2a4a" />
                </div>
                Ajustes Recibidos
              </h2>
            </div>

            {feedbacks.length === 0 ? (
              <div style={{
                padding: '36px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '13px',
                background: 'rgba(255, 255, 255, 0.35)',
                borderRadius: '16px',
                border: '1px dashed rgba(203, 213, 225, 0.8)'
              }}>
                No tienes comentarios ni solicitudes de ajuste recientes.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {feedbacks.map((fb: any) => (
                  <div
                    key={fb.id}
                    onClick={() => navigate(`/content?pieceId=${fb.pieceId}`)}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, rgba(254, 242, 242, 0.75) 0%, rgba(255, 255, 255, 0.6) 100%)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(254, 202, 202, 0.7)',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.04)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.6)';
                      e.currentTarget.style.boxShadow = '0 6px 18px rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(254, 202, 202, 0.7)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.04)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      {fb.user?.avatar ? (
                        <img src={fb.user.avatar} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid rgba(255,255,255,0.9)' }} />
                      ) : (
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#ef4444', color: 'white', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {fb.user?.firstName?.[0] || 'U'}
                        </div>
                      )}
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#991b1b' }}>
                        {fb.user?.firstName} {fb.user?.lastName} ({fb.user?.role})
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#0f2a4a', fontWeight: 600, lineHeight: 1.4, marginBottom: '8px' }}>
                      {fb.content || 'Nota de ajuste'}
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      En pieza: <span style={{ color: '#0070f3', textDecoration: 'underline' }}>{fb.piece?.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* 4. RANKING DE PRODUCTIVIDAD Y PULSO DE MARCAS */}
        <div className="dash-section-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          
          {/* RANKING DE COLABORADORES */}
          <div style={{ ...glassCardStyle, padding: '26px 28px' }}>
            <h2 style={{ margin: '0 0 18px', fontSize: '16px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconUsers size={18} color="#10b981" />
              </div>
              Rendimiento del Equipo (Mes)
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {collaborators.slice(0, 5).map((collab: any, idx: number) => (
                <div
                  key={collab.userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: '14px',
                    background: idx === 0
                      ? 'linear-gradient(135deg, rgba(254, 249, 195, 0.65) 0%, rgba(255, 255, 255, 0.75) 100%)'
                      : idx === 1
                      ? 'linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(255, 255, 255, 0.75) 100%)'
                      : idx === 2
                      ? 'linear-gradient(135deg, rgba(255, 237, 213, 0.55) 0%, rgba(255, 255, 255, 0.75) 100%)'
                      : 'rgba(255, 255, 255, 0.55)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: idx === 0
                      ? '1px solid rgba(250, 204, 21, 0.6)'
                      : idx === 1
                      ? '1px solid rgba(203, 213, 225, 0.85)'
                      : idx === 2
                      ? '1px solid rgba(251, 146, 60, 0.5)'
                      : '1px solid rgba(255, 255, 255, 0.7)',
                    boxShadow: idx === 0
                      ? '0 3px 12px rgba(234, 179, 8, 0.12)'
                      : '0 2px 8px rgba(15, 42, 74, 0.03)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {MEDAL_EMOJIS[idx] ? (
                      <img
                        src={MEDAL_EMOJIS[idx]}
                        alt={`Puesto ${idx + 1}`}
                        style={{
                          width: '26px',
                          height: '26px',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 900,
                        color: '#64748b',
                        width: '26px',
                        height: '26px',
                        borderRadius: '8px',
                        background: 'rgba(100, 116, 139, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        #{idx + 1}
                      </span>
                    )}
                    {collab.avatar ? (
                      <img src={collab.avatar} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid rgba(255,255,255,0.9)' }} />
                    ) : (
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #0070f3 0%, #2688c9 100%)', color: 'white', fontSize: '12px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.9)' }}>
                        {collab.name?.[0] || 'C'}
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a' }}>{collab.name}</div>
                      <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{collab.role}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: 900, color: '#15803d', display: 'inline-flex' }}>
                      <RouletteNumber value={collab.approved} duration={1200} />
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '4px', fontWeight: 600 }}>unidades</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CUMPLIMIENTO POR MARCA */}
          <div style={{ ...glassCardStyle, padding: '26px 28px' }}>
            <h2 style={{ margin: '0 0 18px', fontSize: '16px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(0, 112, 243, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconSparkles size={18} color="#0070f3" />
              </div>
              Estado de Entrega por Marca
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {brandsProgress.slice(0, 5).map((b: any, idx: number) => (
                <AnimatedBrandProgress key={b.id || idx} brand={b} index={idx} />
              ))}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Dashboard;